import { useState } from 'react';

import { cn } from '@/lib/cn';
import { qbotCardIconUrl } from '@/services/qbotApi';

interface CardIconProps {
  id: number;
  name?: string;
  /** 'base' (default), 'evolution', or 'hero' form */
  form?: 'base' | 'evolution' | 'hero';
  /** Display level (1-14) — rendered as an overlay badge if provided */
  level?: number;
  /** Tailwind size class — defaults to a 72px square that fits the 4x2 grid */
  className?: string;
  /** Optional click handler — used by deck builder to swap a card */
  onClick?: () => void;
}

/**
 * Single card icon rendered from qbot-api `/api/v1/cr/cards/:id/icon`.
 *
 * The PNG is served with `Cache-Control: public, max-age=604800, immutable`
 * so we let the browser cache it — no extra in-app cache layer needed.
 *
 * On image error we fall back to the card name as text. Real Clash Royale
 * cards rarely go missing, but evolution/hero forms aren't always synced
 * on the server side and would otherwise show a broken image icon.
 */
export function CardIcon({
  id,
  name,
  form = 'base',
  level,
  className,
  onClick,
}: CardIconProps) {
  const [failed, setFailed] = useState(false);
  const interactive = Boolean(onClick);

  if (failed) {
    return (
      <div
        className={cn(
          'flex aspect-square items-center justify-center rounded-md bg-bg-tertiary text-[10px] text-fg-muted',
          className,
        )}
        title={name}
      >
        {name ?? `#${id}`}
      </div>
    );
  }

  const ringClass = form === 'hero'
    ? 'ring-2 ring-yellow-400/80'
    : form === 'evolution'
      ? 'ring-2 ring-fuchsia-400/80'
      : '';

  const content = (
    <div className={cn('relative aspect-square overflow-hidden rounded-md bg-bg-tertiary', ringClass, className)}>
      <img
        src={qbotCardIconUrl(id, form)}
        alt={name ?? `Card ${id}`}
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        className="h-full w-full object-contain"
      />
      {typeof level === 'number' && (
        <span
          className={cn(
            'absolute bottom-0.5 right-0.5 rounded px-1 text-[10px] font-bold tabular-nums leading-tight',
            level >= 14 ? 'bg-yellow-500 text-black' : 'bg-black/70 text-white',
          )}
        >
          {level}
        </span>
      )}
    </div>
  );

  if (interactive) {
    return (
      <button type="button" onClick={onClick} className="block">
        {content}
      </button>
    );
  }
  return content;
}
